import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  TEntityActions,
  TRectDropElement,
  TTimelineConfiguration,
  TTimelineIndicators,
  TTimelineModel,
  TToolbarAction,
} from './model/timeline.types';
import { TTimelineChartGroups, createTimelineChart } from './model/d3/draw';
import { addMarkers, addStripEvent, addStripOnCursor, removeCursor } from './model/d3/events/events';
import { TIMELINE_D3 } from './model/draw.model';
import { TUserPaginationOption } from '../listing-pagination/pagination.types';
import { TPaginationInfo, TLL_RECALIBRATE_TYPES } from '@monorepo/types';
import { ModalController, PopoverController } from '@ionic/angular';

export type TToolbarActionEventEmit = [Exclude<TToolbarAction, 'SEARCH'>, Event] | ['SEARCH', Event | null];

@Component({
  selector: 'monorepo-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements AfterViewInit {
  #chartGroup: TTimelineChartGroups | null = null;

  timelineView: TTimelineModel['boardView']['boardViewType'] = 'WEEKLY';
  hideLeftPanel?: boolean = undefined;

  // Setting Timeline model
  _timelineModel!: TTimelineModel;
  @Input() set timelineModel(value: TTimelineModel) {
    this._timelineModel = value;
    this.#renderTimeline(this._timelineModel);
    this.timelineView = value.boardView.boardViewType;
    this.hideLeftPanel = value.hideLeftPanel;
  }

  // Pagination inputs
  @Input() paginationOption?: TUserPaginationOption;
  @Input() paginationInfo?: TPaginationInfo;

  // Toolbard options
  @Input() toolbarActions: TToolbarAction[] = [];

  // Render Type to change configuration
  @Input() configuration: TTimelineConfiguration = {
    renderType: 'TNA_BOARD', // Default
  };

  @Input() isFilterActive = false;

  //Only disabling strip Left click and right click events [PLN-160]
  @Input() disableStripEvents = false;

  @Input() hideOpenInfoBtn = false;

  // Strip drag and drop
  #loadStrip: TRectDropElement[] | null = null;

  @Input() set loadStrip(value: TRectDropElement[] | null) {
    if (value) {
      this.#loadStrip = value;
      this.addStrip();
    } else {
      this.#loadStrip = null;
      this.removeStrip();
    }
  }

  _markers: TTimelineIndicators = { indicators: [] };
  @Input() set markers(value: TTimelineIndicators) {
    this._markers = value;
    this.addMarkers();
  }

  // ===================

  // Strip Events
  @Output() stripEvent = new EventEmitter<TEntityActions>();
  @Output() openFilter = new EventEmitter();
  @Output() recalibrate = new EventEmitter<TLL_RECALIBRATE_TYPES>();
  @Output() openInfo = new EventEmitter<Event>();
  @Output() updatedPaginationOption = new EventEmitter<TUserPaginationOption>();

  @Output() onToolbarAction = new EventEmitter<TToolbarActionEventEmit>();
  @Output() onTimelineUpdate = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onTimelineView = new EventEmitter<Event>();
  @Output() onPrev = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onNext = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onToday = new EventEmitter<TTimelineModel['boardView']>();

  @ViewChild('timeline') timelineElm!: ElementRef<HTMLDivElement>;

  #resizeObserver: ResizeObserver;
  constructor(private modalController: ModalController, private pop: PopoverController) {
    this.#resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        this.#renderTimeline(this._timelineModel);
      });
    });
  }

  // Resize window
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    console.log('[RESIZE]', event, this.timelineElm.nativeElement.clientWidth);
    this.#renderTimeline(this._timelineModel);
  }
  // -------------

  // keyboard arrows
  @HostListener('window:keydown', ['$event'])
  async onKeyPress(event: KeyboardEvent) {
    const isModalOpen = await this.modalController.getTop();
    const isPopoverOpen = await this.pop.getTop();
    if (isModalOpen || isPopoverOpen) return;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.onToolbarAction.emit(['SEARCH', null]);
      return;
    }

    if (event.key === 'ArrowLeft') {
      this.onPrev.emit(this._timelineModel.boardView);
    } else if (event.key === 'ArrowRight') {
      this.onNext.emit(this._timelineModel.boardView);
    }
  }
  //

  removeStrip() {
    if (this.#chartGroup) removeCursor(this.#chartGroup);
  }
  addStrip() {
    console.log('Load Order');
    if (this.#chartGroup && this.#loadStrip) {
      const drawModel = TIMELINE_D3.createDrawModel(this.timelineElm, this._timelineModel, this.configuration);
      addStripOnCursor(this.#loadStrip, this.#chartGroup, drawModel, (e) => {
        console.log('Event', e);
        this.stripEvent.emit(e);
      });
    }
  }

  addMarkers() {
    if (this.#chartGroup) {
      const drawModel = TIMELINE_D3.createDrawModel(this.timelineElm, this._timelineModel, this.configuration);
      addMarkers(this.#chartGroup, drawModel, this._markers.indicators);
    }
  }

  changeTimelineView($event: Event) {
    const event = $event as unknown as { detail: { value: 'DAILY' | 'WEEKLY' | 'MONTHLY' } };
    const value = event.detail.value;
    this.onTimelineUpdate.emit({
      ...this._timelineModel.boardView,
      boardViewType: value,
    });
  }

  #renderTimeline(timelineModel: TTimelineModel) {
    // Create Draw Model only if the timeline elm is defined
    if (this.timelineElm && this.timelineElm.nativeElement) {
      const drawModel = TIMELINE_D3.createDrawModel(this.timelineElm, timelineModel, this.configuration);
      this.#chartGroup = createTimelineChart(drawModel, (e) => {
        console.log('STRIP Info', e);
        this.stripEvent.emit(e);
      });

      addStripEvent(this.#chartGroup, drawModel, (timelineEventType) => {
        console.log('STRIP Info', timelineEventType);
        if (!this.disableStripEvents) {
          this.stripEvent.emit(timelineEventType);
        }
      });
      this.addStrip();
      this.addMarkers();
      this.#scrollFirstHighlightedStripIntoView();
    } else {
      console.log('[ERROR] Timeline Elment should be defiend');
    }
  }

  #scrollFirstHighlightedStripIntoView() {
    const firstHighlightedStrip = this._timelineModel?.entities
      .flatMap((g) => g.entities)
      .flatMap((e) => e.elements)
      .find((el) => el.type === 'STRIP' && el.isStripHighlighted);
    if (!firstHighlightedStrip) return;
    requestAnimationFrame(() => {
      const stripElement = document.getElementById(firstHighlightedStrip.id);
      if (stripElement) {
        stripElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });
  }

  ngAfterViewInit(): void {
    this.#renderTimeline(this._timelineModel);
    this.#resizeObserver.observe(this.timelineElm.nativeElement);
  }

  updateUserPagination($event: TUserPaginationOption) {
    this.paginationOption = $event;
    this.updatedPaginationOption.emit($event);
  }

  print() {
    const mywindow = window.open('', 'PRINT', 'height=400,width=600');

    if (mywindow) {
      mywindow.document.write('<html><head><title>' + document.title + '</title>');
      mywindow.document.write(`<style>
      body {
        font-family: Roboto, "Helvetica Neue", sans-serif;
        padding:0;
        margin:0;
      }
      @media print{@page {size: landscape}}
      .timeline-wrapper {
        overflow: visible !important;
      }
      </style></head><body>`);
      mywindow.document.write(this.timelineElm.nativeElement.innerHTML);
      mywindow.document.write('</body></html>');

      // mywindow.document.close(); // necessary for IE >= 10
      // mywindow.focus(); // necessary for IE >= 10*/

      mywindow.print();
      // mywindow.close();

      return true;
    }
    return false;
  }
}
